import { Builder } from "@builder.io/react";
export const sectionVariants = ["A", "B"];
export const buttonStyle = [  
  "primary",
  "secondary",
  "outline",
  "ghost",
  "link",
  "link-reserve",
  "link-reserve-dark",
];
export const sectionSpacing = ["none", "small", "medium", "large", "xlarge"];
export const sectionSpacingHero = ["none", "small", "medium", "large", "xlarge"];

export const allowedFileTypes = ["jpeg", "jpg", "png", "svg", "webp"];
export const allowedFileTypesVideo = ["mp4", "webm", "mov", "ogg"];
export const allowedParagraphFontSizes = ['small', 'base', 'large'];
export const selectableColors = ["brand", "primary", "secondary"];
export const allowedBulletTypes = ["none", "disc", "decimal", "brand", "check"];
export const allowedImageRatios = ["16/9", "4/3", "3/4", "9/16", "1/1", "open"];
export const availablePageTypes = ["case-study", "market-insights", "business-guides"];

// Type definitions for input fields
type InputField = {
  name: string;
  type: string;
  friendlyName?: string;
  helperText?: string;
  defaultValue?: string | boolean;
  allowedFileTypes?: string[];
  enum?: string[];
  showIf?: string;
  required?: boolean;
};

const buttonInputs = [
  {
    name: "hasButton",
    type: "boolean",
    defaultValue: true,
  },
  {
    name: "buttonText",
    type: "text",
    defaultValue: "Button Text",
    showIf: `options.get('hasButton') === true`,
  },
  {
    name: "buttonLink",
    type: "url",
    defaultValue: "#",
    showIf: `options.get('hasButton') === true`,
  },
  {
    name: "buttonStyle",
    type: "string",
    enum: buttonStyle,
    defaultValue: "primary",
    showIf: `options.get('hasButton') === true`,
  }
];

const secondaryButton = [
  {
    name: "secondaryButton",
    type: "boolean",
    defaultValue: false,
    showIf: `options.get('hasButton') === true`,
  },
  {
    name: "secondaryButtonText",
    type: "text",
    showIf: `options.get('secondaryButton') === true && options.get('hasButton') === true`,
  },
  {
    name: "secondaryButtonLink",
    type: "url",
    showIf: `options.get('secondaryButton') === true && options.get('hasButton') === true`,
  },
  {
    name: "secondaryButtonStyle",
    type: "string",
    enum: buttonStyle,
    showIf: `options.get('secondaryButton') === true && options.get('hasButton') === true`,
  },
];

// Common input configurations
export const commonInputs = {
  baseHero: [
    {
      name: "darkMode",
      type: "boolean",
      defaultValue: false,
    },
    {
      name: "variant",
      type: "string",
      enum: sectionVariants,
      defaultValue: "A",
    },
    {
      name: "spacing",
      type: "string",
      enum: sectionSpacingHero,
      defaultValue: "xlarge",
    },
  ],

  videoImageSelection: [
    {
      name: "mediaType",
      type: "string",
      enum: ["image", "video"],
    },
    {
      name: "image",
      type: "file",
      allowedFileTypes: allowedFileTypes,
      showIf: `options.get('mediaType') === 'image' || options.get('mediaType') === 'video'`,
    },
    {
      name: "imageAltText",
      type: "text",
      showIf: `options.get('mediaType') === 'image' || options.get('mediaType') === 'video'`,
    },
    {
      name: "video",
      type: "url",
      showIf: `options.get('mediaType') === 'video'`,
    },
  ],

  baseSettings: (options: {
    includeSpacing?: boolean;
    includePageNav?: boolean;
  } = {}) => {
    const {
      includeSpacing = true,
      includePageNav = false,
    } = options;

    const inputs: InputField[] = [
      {
        name: "darkMode",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "variant",
        type: "string",
        enum: sectionVariants,
        defaultValue: "A",
      },
      {
        name: "themeChangeOnScroll",
        type: "boolean",
        defaultValue: false,
        helperText: "If true, the WHOLE PAGE THEME will change to dark mode when the user scrolls the section into view. You may see dark theme of the page in the viusal editor - which is normal, please refresh or preview the page to see the correct theme.",
      },
    ];

    if (includeSpacing) {
      inputs.push({
        name: "spacing",
        type: "string",
        enum: sectionSpacing,
        defaultValue: "xlarge",
      });
    }

    if (includePageNav) {
      inputs.push(
        {
          name: "usedInPageNav",
          type: "boolean",
          defaultValue: false,
        },
        {
          name: "pageNavTitle",
          type: "string",
          defaultValue: "Section",
          showIf: "options.get('usedInPageNav') === true",
        }
      );
    }

    return inputs;
  },

  buttonInputs: (options: {
    excludeMicroCopy?: boolean;
  } = {}) => {
    const {
      excludeMicroCopy = false,
    } = options;

    const inputs = [];
    
    inputs.push(...buttonInputs);

    if (!excludeMicroCopy) {
      inputs.push({
        name: "microCopy",
        type: "text",
        defaultValue: "Microcopy",
        showIf: `options.get('hasButton') === true`,
      });
    }

    return inputs;
  },

  secondaryButtonInputs: () => {
    const inputs = [];
    inputs.push(...secondaryButton);
    return inputs;
  },

  // Helper function to create custom content inputs with specific fields
  contentInputs: (options: {
    includeBadge?: boolean;
    includeIcon?: boolean;
    includeTitle?: boolean;
    includeContent?: boolean;
    includeSubtitle?: boolean;
    includeLink?: boolean;
  } = {}) => {
    const {
      includeBadge = false,
      includeIcon = false,
      includeTitle = false,
      includeContent = false,
      includeSubtitle = false,
      includeLink = false,
    } = options;

    const inputs = [];
    
    if (includeBadge) {
      inputs.push({
        name: "badge",
        type: "text",
        defaultValue: "Badge Text",
      });
    }
    if (includeIcon) {
      inputs.push({
        name: "icon",
        type: "string",
        defaultValue: "check_circle",
        helperText: "Icon from Material Icons https://fonts.google.com/icons",
      });
    }
    if (includeTitle) {
      inputs.push({
        name: "title",
        type: "text",
        defaultValue: "Title",
      });
    }
    if (includeSubtitle) {
      inputs.push({
        name: "subtitle",
        type: "richText",
        defaultValue: "Subtitle",
      });
    }
    if (includeContent) {
      inputs.push({
        name: "content",
        type: "richText",
        defaultValue: "Content",
      });
    }
    if (includeLink) {
      inputs.push({
        name: "link",
        type: "url",
        defaultValue: "#",
      });
    }

    return inputs;
  },

  imageInput: (options: {
    name: string;
    friendlyName?: string;
    helperText?: string;
    showIf?: string;
    includeAltText?: boolean;
    includeRatio?: boolean;
    includeLink?: boolean;
    includeCaption?: boolean;
  }): InputField[] => {
    const {
      name,
      friendlyName,
      helperText,
      includeAltText = true,
      includeRatio = false,
      includeLink = false,
      includeCaption = false,
      showIf,
    } = options;
  
    const fields: InputField[] = [
      {
        name,
        type: "file",
        allowedFileTypes: allowedFileTypes,
        ...(friendlyName && { friendlyName }),
        ...(helperText && { helperText }),
        ...(showIf && { showIf }),
        defaultValue: "/placeholder.svg",
      },
    ];
  
    if (includeAltText) {
      fields.push({
        name: `${name}AltText`,
        type: "text",
        ...(showIf && { showIf }),
      });
    }
  
    if (includeRatio) {
      fields.push({
        name: `${name}Ratio`,
        type: "string",
        enum: allowedImageRatios,
        ...(showIf && { showIf }),
      });
    }
  
    if (includeLink) {
      fields.push({
        name: `${name}Link`,
        type: "url",
        ...(showIf && { showIf }),
      });
    }
    
    if (includeCaption) {
      fields.push({
        name: `${name}Caption`,
        type: "text",
        ...(showIf && { showIf }),
      });
    }
    return fields;
  },

  // Testimonial input
  testimonialInput: (options: {
    includeContent?: boolean;
    plainTextContent?: boolean;
    includeAuthor?: boolean;
    includeInitials?: boolean;
    includeCompany?: boolean;
    includeRating?: boolean;
    includeImage?: boolean;
    includeVideo?: boolean;
    includeLink?: boolean;
    includeButton?: boolean;
  } = {}) => {
    const {
      includeContent = false,
      plainTextContent = false,
      includeAuthor = false,
      includeInitials = false,
      includeCompany = false,
      includeLink = false,  
      includeButton = false,
    } = options;

    const inputs = [];

    if (includeContent) {
      if (plainTextContent) {
        inputs.push({
          name: "content",
          type: "longText",
          defaultValue: "Content",
        });
      } else {
        inputs.push({
          name: "content",
          type: "richText",
          defaultValue: "Content",
        });
      }
    }

    if (includeAuthor) {  
      inputs.push({
        name: "author",
        type: "text",
        defaultValue: "Author",
      });
    } 

    if (includeInitials) {
      inputs.push({
        name: "initials",
        type: "text",
        defaultValue: "JD",
        helperText: "Leave blank to use image avatar",
      });
    }

    if (includeCompany) {
      inputs.push({
        name: "company",
        type: "text",
        defaultValue: "Company",
      });
    }

    if (includeLink) {
      inputs.push({
        name: "link",
        type: "url",
      }); 
    }

    if (includeButton) {
      inputs.push(...buttonInputs);
    }

    return inputs;
  },

  menuItemInputs: (options: {
    includeTitle?: boolean;
    includeLink?: boolean;
    includeBold?: boolean;
    includeImage?: boolean;
    includeIcon?: boolean;
    includeButton?: boolean;  
    includeSubtitle?: boolean;
    includeSuffixIcon?: boolean;
  } = {}): InputField[] => {
    const {
      includeTitle = false,
      includeLink = false,
      includeBold = false,
      includeImage = false,
      includeIcon = false,
      includeButton = false,
      includeSubtitle = false,
      includeSuffixIcon = false,
    } = options;

    const inputs: InputField[] = []; 

    if (includeTitle) {
      inputs.push({
        name: "title",
        type: "string",
      });
    }

    if (includeBold) {
      inputs.push({
        name: "bold",
        type: "boolean",
        defaultValue: false,
      });
    }

    if (includeSubtitle) {
      inputs.push({
        name: "subtitle",
        type: "string",
      });
    }

    if (includeLink) {
      inputs.push({
        name: "link",
        type: "url",
      });
    }

    // if (includeSubmenu) {
    //   inputs.push({
    //     name: "submenu",
    //     type: "object",
    //   });
    // }

    if (includeImage) {
      inputs.push({
        name: "hasImage",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "image",
        type: "file",
        allowedFileTypes: allowedFileTypes,
        showIf: `options.get('hasImage') === true`,
      });
    }

    if (includeIcon) {
      inputs.push({
        name: "hasIcon",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "icon",
        type: "string",
        showIf: `options.get('hasIcon') === true`,
        helperText: "Icon from Material Icons https://fonts.google.com/icons",
      });
    }

    if (includeButton) {
      inputs.push({
        name: "hasButton",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "buttonText",
        type: "string",
        showIf: `options.get('hasButton') === true`,
      },
      {
        name: "buttonStyle",
        type: "string",
        showIf: `options.get('hasButton') === true`,
        enum: buttonStyle,
      });
    }

    if (includeSuffixIcon) {
      inputs.push({
        name: "hasSuffixIcon",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "suffixIcon",
        type: "string",
        showIf: `options.get('hasSuffixIcon') === true`,
        helperText: "Icon from Material Icons https://fonts.google.com/icons",
      });
    }

    return inputs;
  }
};

// Helper function to register menu groups
export const registerMenuGroups = (groups: { name: string; items: { name: string }[] }[]) => {
  groups.forEach(group => {
    Builder.register("insertMenu", {
      name: group.name,
      items: group.items
    });
  });
};
